export type PwaInstallHint =
  | 'chrome'
  | 'safari-ios'
  | 'firefox-android'
  | 'edge'
  | 'none';

export interface PwaConfig {
  title: string;
  themeColor: string;
  logoUrl?: string;
}

interface PwaState {
  enabled: boolean;
  showBanner: boolean;
  installHint: PwaInstallHint;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWA_DISMISS_KEY = 'cw_admin_pwa_install_dismissed';
const PWA_ENABLED_KEY = 'cw_admin_pwa_enabled';
const PWA_CONFIG_KEY = 'cw_admin_pwa_config';
const DEFAULT_ICON_192 = '/icons/android-chrome-192x192.png';
const DEFAULT_ICON_512 = '/icons/android-chrome-512x512.png';
const listeners = new Set<() => void>();
let manifestBlobUrl: string | null = null;

let state: PwaState = {
  enabled: false,
  showBanner: false,
  installHint: 'none',
  deferredPrompt: null,
};

let initialized = false;

export function isResellerPwaEnabled(admin: { pwa_enabled?: boolean | null }): boolean {
  return admin.pwa_enabled === true;
}

function removePwaDocumentTags() {
  document.querySelectorAll('link[rel="manifest"]').forEach((node) => node.remove());
  document.querySelector('link[rel="apple-touch-icon"]')?.remove();

  [
    'mobile-web-app-capable',
    'apple-mobile-web-app-capable',
    'apple-mobile-web-app-status-bar-style',
    'apple-mobile-web-app-title',
  ].forEach((name) => {
    document.querySelector(`meta[name="${name}"]`)?.remove();
  });

  if (manifestBlobUrl) {
    URL.revokeObjectURL(manifestBlobUrl);
    manifestBlobUrl = null;
  }

  document.documentElement.classList.remove('pwa-standalone');
}

function clearPwaCache() {
  try {
    sessionStorage.removeItem(PWA_ENABLED_KEY);
    sessionStorage.removeItem(PWA_CONFIG_KEY);
  } catch {
    // ignore
  }
}

export function disablePwaApplication(): void {
  clearPwaCache();
  removePwaDocumentTags();

  state.enabled = false;
  state.showBanner = false;
  state.deferredPrompt = null;
  state.installHint = 'none';
  notify();
}

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribePwa(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPwaState(): Readonly<PwaState> {
  return state;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function getInstallHint(): PwaInstallHint {
  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /ipad|iphone|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !/edg/.test(ua);
  const isSafari = /safari/.test(ua) && !/chrome|crios|fxios|edg/.test(ua);
  const isFirefox = /firefox|fxios/.test(ua);
  const isEdge = /edg/.test(ua);

  if (isIOS && isSafari) {
    return 'safari-ios';
  }
  if (isAndroid && isFirefox) {
    return 'firefox-android';
  }
  if (isEdge) {
    return 'edge';
  }
  if (isChrome || (isAndroid && !isFirefox)) {
    return 'chrome';
  }
  return 'none';
}

function setMeta(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function toAbsoluteUrl(path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }
  return new URL(path, window.location.origin).href;
}

function buildManifestIcons(logoUrl?: string) {
  const icon192 = toAbsoluteUrl(logoUrl || DEFAULT_ICON_192);
  const icon512 = toAbsoluteUrl(logoUrl || DEFAULT_ICON_512);

  return [
    {
      src: icon192,
      sizes: '192x192',
      type: 'image/png',
      purpose: 'any maskable',
    },
    {
      src: icon512,
      sizes: '512x512',
      type: 'image/png',
      purpose: 'any maskable',
    },
  ];
}

function buildManifestJson(config: PwaConfig) {
  const shortName = (config.title || 'Admin').slice(0, 12);

  return {
    name: config.title || 'Admin Dashboard',
    short_name: shortName,
    theme_color: config.themeColor || '#333333',
    background_color: '#ffffff',
    display: 'standalone',
    display_override: ['fullscreen', 'standalone', 'minimal-ui'],
    scope: '/',
    start_url: '/',
    icons: buildManifestIcons(config.logoUrl),
  };
}

function applyManifestLink(config: PwaConfig) {
  if (manifestBlobUrl) {
    URL.revokeObjectURL(manifestBlobUrl);
    manifestBlobUrl = null;
  }

  const manifestJson = JSON.stringify(buildManifestJson(config));
  manifestBlobUrl = URL.createObjectURL(
    new Blob([manifestJson], { type: 'application/manifest+json' })
  );

  let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.href = manifestBlobUrl;
}

function applyPwaDocument(config: PwaConfig) {
  const shortName = (config.title || 'Admin').slice(0, 12);

  applyManifestLink(config);

  setMeta('theme-color', config.themeColor);
  setMeta('mobile-web-app-capable', 'yes');
  setMeta('apple-mobile-web-app-capable', 'yes');
  setMeta('apple-mobile-web-app-status-bar-style', 'black-translucent');
  setMeta('apple-mobile-web-app-title', shortName);
  document.title = config.title || 'Admin Dashboard';

  let appleIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
  if (!appleIcon) {
    appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    document.head.appendChild(appleIcon);
  }
  appleIcon.href = config.logoUrl || DEFAULT_ICON_192;

  applyStandaloneDocumentClass();
}

function cachePwaConfig(config: PwaConfig) {
  try {
    sessionStorage.setItem(PWA_ENABLED_KEY, 'true');
    sessionStorage.setItem(PWA_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function bootstrapPwaFromCache(): boolean {
  try {
    if (sessionStorage.getItem(PWA_ENABLED_KEY) !== 'true') {
      return false;
    }

    const raw = sessionStorage.getItem(PWA_CONFIG_KEY);
    if (raw) {
      applyPwaDocument(JSON.parse(raw) as PwaConfig);
    } else {
      applyPwaDocument({
        title: 'Admin Dashboard',
        themeColor: '#333333',
      });
    }

    return true;
  } catch {
    return false;
  }
}

function applyStandaloneDocumentClass() {
  if (isStandalone()) {
    document.documentElement.classList.add('pwa-standalone');
  } else {
    document.documentElement.classList.remove('pwa-standalone');
  }
}

function updateInstallBannerVisibility() {
  if (state.enabled !== true || isStandalone()) {
    state.showBanner = false;
    notify();
    return;
  }

  state.showBanner = shouldShowBanner() && state.installHint !== 'none';
  notify();
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    await navigator.serviceWorker.register('/pwa-sw.js', { scope: '/' });
  } catch {
    // Non-fatal — install prompt may still work on some platforms.
  }
}

function shouldShowBanner(): boolean {
  if (isStandalone()) {
    return false;
  }
  try {
    return localStorage.getItem(PWA_DISMISS_KEY) !== '1';
  } catch {
    return true;
  }
}

function bindStandaloneGuards() {
  document.addEventListener(
    'click',
    (event) => {
      if (!isStandalone()) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor || !anchor.href) {
        return;
      }

      const url = new URL(anchor.href, window.location.origin);
      if (url.origin === window.location.origin) {
        return;
      }

      if (anchor.target === '_blank' || url.protocol === 'http:' || url.protocol === 'https:') {
        const confirmed = window.confirm(
          'This link opens an external page. Continue?'
        );
        if (!confirmed) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    true
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!isStandalone()) {
        return;
      }
      const touchEvent = event as TouchEvent & { scale?: number };
      if (touchEvent.scale !== undefined && touchEvent.scale !== 1) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  window.matchMedia('(display-mode: standalone)').addEventListener('change', applyStandaloneDocumentClass);
  window.matchMedia('(display-mode: fullscreen)').addEventListener('change', applyStandaloneDocumentClass);
}

function bindInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    if (state.enabled !== true) {
      return;
    }

    event.preventDefault();
    state.deferredPrompt = event as BeforeInstallPromptEvent;
    updateInstallBannerVisibility();
  });
}

function buildPwaConfigFromReseller(reseller: {
  admin: {
    title?: string;
    colors?: { primary?: string };
    logo_icon?: string;
  };
  name?: string;
}): PwaConfig {
  return {
    title: reseller.admin.title || reseller.name || 'Admin Dashboard',
    themeColor: reseller.admin.colors?.primary || '#333333',
    logoUrl: reseller.admin.logo_icon || undefined,
  };
}

export function refreshPwaApplication(config: PwaConfig): void {
  cachePwaConfig(config);
  applyPwaDocument(config);
  state.enabled = true;
  state.installHint = getInstallHint();
  updateInstallBannerVisibility();
}

export async function initPwa(config: PwaConfig): Promise<void> {
  refreshPwaApplication(config);

  if (initialized) {
    return;
  }
  initialized = true;

  bindStandaloneGuards();
  bindInstallPrompt();
  await registerServiceWorker();
}

export function syncPwaForReseller(reseller: {
  admin: {
    pwa_enabled?: boolean | null;
    title?: string;
    colors?: { primary?: string };
    logo_icon?: string;
  };
  name?: string;
}): void {
  if (!isResellerPwaEnabled(reseller.admin)) {
    disablePwaApplication();
    return;
  }

  const config = buildPwaConfigFromReseller(reseller);
  if (initialized) {
    refreshPwaApplication(config);
    return;
  }

  initPwa(config).catch(() => undefined);
}

export function dismissInstallBanner(): void {
  try {
    localStorage.setItem(PWA_DISMISS_KEY, '1');
  } catch {
    // ignore
  }
  state.showBanner = false;
  notify();
}

export async function triggerInstall(): Promise<void> {
  if (state.enabled !== true) {
    return;
  }

  const prompt = state.deferredPrompt;
  if (!prompt) {
    return;
  }

  await prompt.prompt();
  await prompt.userChoice;
  state.deferredPrompt = null;
  dismissInstallBanner();
}
